/**
 * A primitive representation of resource paths that is useful for consistent and reliable
 * definitions for routes based on configuration.
 *
 * This primitive is immutable so that it is easy to work with without having to consider edge cases.
 *
 * **Note:** Automatically filters empty string paths, Example:
 * `Path.create('a', '', 'b', '', 'c', '')` is identical to `Path.create('a', 'b', 'c')`
 * and would yield the string "/a/b/c"
 */
class Path {
  private readonly _parts: readonly string[];

  private constructor(parts: readonly string[]) {
    this._parts = parts
      .map(p => this.removeSlashCharacters(p))
      .filter(p => this.isNotEmptyString(p));
  }

  private removeSlashCharacters(part: string): string {
    return part.replace('/', '') ?? '';
  }

  private isNotEmptyString(part: string): boolean {
    return part !== '';
  }

  static fromArray(parts: string[]): Path {
    return new Path(parts);
  }

  static create(...parts: string[]) {
    return Path.fromArray(parts);
  }

  /**
   * Example: `Path.fromString('/path/to/my/resource')`
   */
  static fromString(pathString: string): Path {
    const parts = pathString?.split('/') ?? [];
    return new Path(parts);
  }

  append(...parts: string[]): Path {
    return Path.fromArray([...this._parts, ...parts]);
  }

  /**
   * Example:
   * ```
   * Path.create('path', 'to', 'my', 'resource').toArray()
   * ```
   * yields:
   * ```
   * ['path', 'to', 'my', 'resource']
   * ```
   */
  toArray(): string[] {
    return ['/', ...this._parts];
  }

  /**
   * Example:
   * ```
   * Path.create('path', 'to', 'my', 'resource').toString()
   * ```
   * yields:
   * ```
   * "/path/to/my/resource"
   * ```
   */
  toString(): string {
    return `/${this._parts.join('/')}`;
  }
}

export default Path;
